import type { CarriedLine } from "../hud/panels";
import type { Hands, HeldThing } from "../hud/panels/shared";

/*
 * Arms and armour, client-side: what the player may shoot with, what they have
 * on, and the words for both.
 *
 * A module beside `witness/witness.ts` rather than more of it (Phase 7's module
 * rule, `docs/rust-best-practices.md` §3.1 applied to `web/src`): the witnessing
 * layer was at 2,565 code lines when this chunk opened and the archery verb
 * would have put it within sight of the split line on its own. What lives here
 * is everything about the bow and the breastplate that is a *rule about words
 * and presses* rather than a piece of the witnessing loop — which turns out to
 * be all of it except the two lines that queue the commands.
 *
 * # Three rules this file is written under, all of them the phase's
 *
 * - **No press the simulation refuses in silence** (the graybox ruling). Every
 *   refusal below is a rule the sim holds and this client has the number for:
 *   the bow's range and cooldown cross the wall as constants, and what is in the
 *   hands is a read. A press that would do nothing says why instead.
 * - **The simulation owns the words.** Every noun in a line here — *oak bow*,
 *   *iron breastplate* — arrives from `Snapshot::item_label` whole. This file
 *   writes the punctuation and the connective tissue and never a noun.
 * - **Nothing here knows what a blow does.** The prompt says what the press
 *   *will do*; what it *did* is read off the world afterwards.
 */

/** The refusal vocabulary for the bow, held to the strike verb's own rule. */
export const REFUSED_NO_BOW = "you have no bow";
export const REFUSED_NO_ARROWS = "your quiver is empty";
export const REFUSED_OUT_OF_RANGE = "too far to shoot";
export const REFUSED_NOBODY_TO_SHOOT = "nobody in front of you";
export const REFUSED_STILL_DRAWING = "you are still drawing";

/** And the two the hands panel's own verbs give. */
export const REFUSED_NOTHING_TO_WEAR = "you have nothing to put on";
export const REFUSED_NOTHING_WORN = "you are wearing nothing";

/**
 * *A child cannot fight*, and it lives here rather than beside the swing.
 *
 * **One sentence, one owner.** The blade and the bow are refused by the same
 * life-stage row (`sim_core::body`'s `LifeStage::fights`), so they are refused in
 * the same words — and two copies of one string is how a rename leaves half a
 * client saying something else. `witness/witness.ts` imports it for the swing.
 */
export const REFUSED_A_CHILD_CANNOT_FIGHT = "a child cannot fight";

/**
 * What the player has to shoot with, as this client reads it.
 *
 * The simulation's answer to two questions in one call
 * ({@link WitnessSource.quiver}), kept as a pair here because the *refusals* are
 * two different sentences: a player with no bow and a player with no arrows are
 * told different things.
 */
export interface Quiver {
  /** The bow's handle, or `null` for a pair of hands with no bow in them. */
  readonly bow: number | null;

  /** How many arrows are left. */
  readonly arrows: number;
}

/** Everything the loose prompt and the loose press are decided from. */
export interface LooseState {
  /** What the crosshair is on, or `null` for a shot at nobody. */
  readonly target: { readonly label: string; readonly inRange: boolean } | null;

  /** What the hands hold. */
  readonly quiver: Quiver;

  /** Whether the stage table lets this body fight at all. */
  readonly mayFight: boolean;

  /** Whether the cooldown has passed. */
  readonly ready: boolean;
}

/**
 * Returns why a loose would be refused, or `null` when it would go.
 *
 * **The order is the simulation's own** (`sim_core::combat`'s `loose`, whose
 * doc lists the nine questions in the order it asks them), and the order is
 * load-bearing rather than tidy: a player with no bow standing out of range
 * should be told about the bow, because that is the thing they can do something
 * about first.
 *
 * The one clause that is not the simulation's is *a child cannot fight*, which
 * is asked first here because it is the one refusal that is about the player
 * rather than about the shot — a child told *"your quiver is empty"* would go
 * and fletch arrows for nothing.
 */
export function looseRefusal(state: LooseState): string | null {
  if (!state.mayFight) {
    return REFUSED_A_CHILD_CANNOT_FIGHT;
  }

  if (state.quiver.bow === null) {
    return REFUSED_NO_BOW;
  }

  if (state.quiver.arrows === 0) {
    return REFUSED_NO_ARROWS;
  }

  if (state.target === null) {
    return REFUSED_NOBODY_TO_SHOOT;
  }

  if (!state.target.inRange) {
    return REFUSED_OUT_OF_RANGE;
  }

  return state.ready ? null : REFUSED_STILL_DRAWING;
}

/**
 * Returns the line under the crosshair for the bow, or `null` for a frame with
 * nothing to say about it.
 *
 * **Drawn whenever the player is aiming at a body and holding a bow**, which is
 * the same rule the swing prompt is drawn under and for its reason: a permanent
 * line for the ninety-nine per cent of a session spent looking at hillsides is
 * chrome, and chrome is what a player stops reading. A body in the crosshair
 * with no bow in the hands says nothing either — *"you have no bow"* over every
 * neighbour a player ever looks at would be the same chrome.
 *
 * `key` is what the player presses, and `null` is a touch session where the verb
 * is a button under the thumb (`witness/interact.ts`'s `promptFor` makes the
 * same split for the same reason).
 */
export function loosePrompt(state: LooseState, key: string | null = "R"): string | null {
  if (state.target === null || state.quiver.bow === null) {
    return null;
  }

  const press = key === null ? "" : `${key} — `;
  const refusal = looseRefusal(state);

  // The cooldown is shown as **readiness** rather than as a refusal, which is
  // the graybox ruling's own point: a key that quietly does nothing teaches a
  // player to stop pressing it, and *(drawing)* is a promise that the next press
  // lands.
  if (refusal === REFUSED_STILL_DRAWING) {
    return `${press}loose at ${state.target.label} (drawing)`;
  }

  if (refusal !== null) {
    return `${press}loose · ${refusal}`;
  }

  // The count is on the line because it is the thing a player runs out of
  // without noticing, and because the simulation is the only thing that knows
  // it.
  return `${press}loose at ${state.target.label} · ${arrowCount(state.quiver.arrows)}`;
}

/*
 * **The swing prompt does not name the thing in your hand, and that is a refusal
 * rather than a fence** (chunk U-5).
 *
 * The brief asked for it: the wield door is in, `WitnessSource.wielded` says
 * what the player chose, so *"strike Halvard with an iron blade"* looks like one
 * label away. It is not, and the simulation says so in as many words — what is
 * in the hand and **what you would swing with are different questions**.
 * `Command::Wield` asks nothing of the thing, wielding a boulder is legal, and
 * what it means is that `combat::weapon_of` walks *past* the boulder and swings
 * whatever is behind it or a fist (`docs/decisions/player.md` §24.3). A prompt
 * that named the wielded thing would therefore be wrong exactly when a player
 * most needs it to be right, and it would be wrong in the client's own voice.
 *
 * `combat::weapon_of` is private and `combat.rs` was out of the sim chunk's
 * bounds, so the read does not exist (§24.8 files it: one `pub(crate)` and one
 * `Snapshot` read). Until it does, what a surface may honestly draw is the thing
 * in the hand — which the inventory page draws, on the row that is in it — and
 * nothing more. Filed at `docs/decisions/ui.md` §6 with the seam named.
 *
 * The bow's prompt has the same shape and the same answer: {@link LooseState}
 * carries the bow's *handle* and no label, and `bow_of` is the simulation's pick
 * out of the carry order rather than the player's choice, so *"loose with your
 * oak bow"* would be the same guess made about a different verb.
 */

/**
 * Returns the hands panel's lines: what is carried, then what is worn, then the
 * quiver.
 *
 * **One panel rather than three**, because a player asking *"what have I got"*
 * is asking one question — and because the panel is the *menu* the wear verbs
 * live in (the owner's UI rule: identity and choices live in menus, in-world
 * beats only confirm). The section headings are the only strings this file
 * writes; every label inside them is the simulation's.
 *
 * The carried section keeps its marker on the first row, because that is still
 * the row the drop verb takes and the panel's hint still says so. The worn
 * section's first row is the one the doff verb takes, and it is marked for
 * exactly the same reason.
 */
export function handsLines(
  carried: readonly string[],
  worn: readonly string[],
  quiver: Quiver,
): CarriedLine[] {
  const lines: CarriedLine[] = carried.map((label) => ({ label }));

  if (worn.length > 0) {
    lines.push({ label: "" }, { label: "worn:" });

    for (const label of worn) {
      lines.push({ label });
    }
  }

  if (quiver.bow !== null || quiver.arrows > 0) {
    lines.push({ label: "" }, { label: arrowCount(quiver.arrows) });
  }

  return lines;
}

/**
 * Returns *"three arrows"* — or *"no arrows"*, which is the sentence that
 * matters.
 *
 * A count in words up to a handful and a numeral past it, which is this client's
 * standing register for small numbers a player reads at a glance. It is the one
 * place in this file that composes a noun, and it composes an *English* one
 * rather than an item's: what an arrow is called is `itemLabel`'s, and this is
 * how many of them there are.
 */
export function arrowCount(arrows: number): string {
  const words = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight"];
  const count = arrows < words.length ? words[arrows] : String(arrows);

  return `${count} ${arrows === 1 ? "arrow" : "arrows"}`;
}

/**
 * The arrow line, or the empty string for a pair of hands with no bow in them
 * and no arrows left.
 *
 * {@link handsLines}' own clause, lifted out so the structured read can carry it
 * as one string. The gate is the same one it always was — a quiver section is
 * drawn for somebody who has *either* half of a shot — and it matters because
 * "no arrows" is a sentence worth saying to a player holding a bow and no
 * sentence at all to one who has never fletched anything.
 */
export function quiverLine(quiver: Quiver): string {
  return quiver.bow !== null || quiver.arrows > 0 ? arrowCount(quiver.arrows) : "";
}

/**
 * Everything the inventory page draws, as one record (chunk U-5).
 *
 * **{@link handsLines}' replacement, and the shape chunk U-3 filed** when it
 * found the page's count counting a helm against a cap that only bounds a pair
 * of hands (`docs/decisions/ui.md` §4.5): what the page was handed was a flat
 * list punctuated by blank rows, so it had to read this file's punctuation to
 * know where the hands ended. It is said once here instead, and the arithmetic
 * on the other side goes.
 *
 * The composition this file does is the same it always did and no more: the
 * arrow line, and nothing else. Every label is the simulation's own
 * ({@link WitnessSource.itemLabel}), every handle is the simulation's own
 * display number, and the two doors on the far side name a thing by that number
 * against the player's own hands and nowhere else
 * (`docs/decisions/player.md` §24.3).
 *
 * `carried` arrives in the simulation's **carry order, with the wielded thing at
 * the head of it** (§24.2), which is why the page's top row is still the one the
 * drop verb takes.
 */
export function handsHeld(hands: {
  readonly carried: readonly HeldThing[];
  readonly worn: readonly HeldThing[];
  readonly wielded: number | null;
  readonly wearable: number | null;
  readonly quiver: Quiver;
  readonly company: readonly string[];
  readonly capacity: number;
}): Hands {
  return {
    carried: hands.carried,
    worn: hands.worn,
    wielded: hands.wielded,
    wearable: hands.wearable,
    quiver: quiverLine(hands.quiver),
    company: hands.company,
    capacity: hands.capacity,
  };
}
